import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  HiOutlineMagnifyingGlass,
  HiOutlinePencilSquare,
  HiOutlineEye,
  HiOutlineUser,
  HiOutlineCalendarDays,
  HiOutlineFolder,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";

import DossierEditModal from "../../../components/dossiers/DossierEditModal";

import {
  getDossiers,
  setDossierPage,
} from "../../../redux/features/dossiers/dossierSlice";

import "./DossiersPage.css";

export default function DossiersPage() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);

  const { dossiers, pagination, isLoading, error } = useSelector(
    (state) => state.dossiers,
  );

  const [search, setSearch] = useState("");

  const [status, setStatus] = useState("");

  const [currentStep, setCurrentStep] = useState("");

  const [selectedCoach, setSelectedCoach] = useState("");

  const [selectedDossier, setSelectedDossier] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const isCoach =
    user?.role === "POWER_INTERNAL_COACH" ||
    user?.role === "POWER_EXTERNAL_COACH";

  /**
   * =====================================================
   * LOAD DOSSIERS
   * =====================================================
   */

  useEffect(() => {
    dispatch(
      getDossiers({
        page: pagination?.page || 1,
        limit: 20,
      }),
    );
  }, [dispatch, pagination?.page]);

  /**
   * =====================================================
   * COACH OPTIONS
   * =====================================================
   */

  const coachOptions = useMemo(() => {
    const map = new Map();

    dossiers.forEach((dossier) => {
      if (dossier?.coach?._id) {
        map.set(dossier.coach._id, dossier.coach);
      }
    });

    return Array.from(map.values());
  }, [dossiers]);

  /**
   * =====================================================
   * FILTERED DOSSIERS
   * =====================================================
   */

  const filteredDossiers = useMemo(() => {
    return dossiers.filter((dossier) => {
      /**
       * SEARCH
       */

      const fullName =
        dossier?.client?.display_name ||
        `${dossier?.client?.first_name || ""} ${
          dossier?.client?.last_name || ""
        }`;

      const matchesSearch = fullName
        .toLowerCase()
        .includes(search.toLowerCase());

      /**
       * STATUS
       */

      const matchesStatus = status ? dossier.status === status : true;

      /**
       * STEP
       */

      const matchesStep = currentStep
        ? dossier.current_step === currentStep
        : true;

      /**
       * COACH
       */

      const matchesCoach = selectedCoach
        ? dossier?.coach?._id === selectedCoach
        : true;

      return matchesSearch && matchesStatus && matchesStep && matchesCoach;
    });
  }, [dossiers, search, status, currentStep, selectedCoach]);

  /**
   * =====================================================
   * PAGINATION
   * =====================================================
   */

  const handlePreviousPage = () => {
    if (pagination.page > 1) {
      dispatch(setDossierPage(pagination.page - 1));
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      dispatch(setDossierPage(pagination.page + 1));
    }
  };

  /**
   * =====================================================
   * EDIT
   * =====================================================
   */

  const openEditModal = (dossier) => {
    setSelectedDossier(dossier);

    setIsEditModalOpen(true);
  };

  /**
   * =====================================================
   * STATUS CLASS
   * =====================================================
   */

  const getStatusClass = (statusValue) => {
    switch (statusValue) {
      case "active":
        return "dossiers-page__status--active";

      case "paused":
        return "dossiers-page__status--paused";

      case "completed":
        return "dossiers-page__status--completed";

      case "cancelled":
        return "dossiers-page__status--cancelled";

      default:
        return "";
    }
  };

  /**
   * =====================================================
   * LOADING
   * =====================================================
   */

  if (isLoading && dossiers.length === 0) {
    return <PageLoader title="Dossiers laden..." />;
  }

  return (
    <div className="dossiers-page">
      <PageHeader
        eyebrow="POWER"
        title="Dossiers"
        description="Overzicht van alle POWER dossiers."
      />

      {error && <div className="dossiers-page__error">{error}</div>}

      {/* =====================================================
          FILTERS
      ===================================================== */}

      <PageCard className="dossiers-page__filters">
        <div className="dossiers-page__filters-grid">
          {/* SEARCH */}

          <div className="dossiers-page__search">
            <HiOutlineMagnifyingGlass />

            <input
              type="text"
              placeholder="Zoek cliënt..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* STATUS */}

          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Alle statussen</option>

            <option value="active">Actief</option>

            <option value="paused">Gepauzeerd</option>

            <option value="completed">Afgerond</option>

            <option value="cancelled">Geannuleerd</option>
          </select>

          {/* STEP */}

          <select
            value={currentStep}
            onChange={(e) => setCurrentStep(e.target.value)}
          >
            <option value="">Alle stappen</option>

            <option value="aanmelding">Aanmelding</option>

            <option value="intake">Intake</option>

            <option value="begeleiding">Begeleiding</option>

            <option value="afronding">Afronding</option>
          </select>

          {/* COACH */}

          {!isCoach && (
            <select
              value={selectedCoach}
              onChange={(e) => setSelectedCoach(e.target.value)}
            >
              <option value="">Alle coaches</option>

              {coachOptions.map((coach) => (
                <option key={coach._id} value={coach._id}>
                  {coach.first_name} {coach.last_name}
                </option>
              ))}
            </select>
          )}
        </div>
      </PageCard>

      {/* =====================================================
          EMPTY
      ===================================================== */}

      {!isLoading && filteredDossiers.length === 0 && (
        <PageEmpty
          icon={<HiOutlineFolder />}
          title="Geen dossiers gevonden"
          description="Er zijn momenteel geen dossiers beschikbaar."
        />
      )}

      {/* =====================================================
          GRID
      ===================================================== */}

      {filteredDossiers.length > 0 && (
        <div className="dossiers-page__grid">
          {filteredDossiers.map((dossier) => (
            <PageCard key={dossier._id} className="dossiers-page__card">
              {/* TOP */}

              <div className="dossiers-page__card-top">
                <div>
                  <h3>{dossier?.client?.display_name}</h3>

                  <span>Dossier #{dossier.sequence_number}</span>
                </div>

                <div
                  className={`dossiers-page__status ${getStatusClass(
                    dossier.status,
                  )}`}
                >
                  {dossier.status}
                </div>
              </div>

              {/* META */}

              <div className="dossiers-page__meta">
                <div>
                  <strong>
                    <HiOutlineUser />
                    Coach
                  </strong>

                  <span>
                    {dossier?.coach?.first_name} {dossier?.coach?.last_name}
                  </span>
                </div>

                <div>
                  <strong>
                    <HiOutlineCalendarDays />
                    Schooljaar
                  </strong>

                  <span>{dossier?.school_year?.name}</span>
                </div>

                <div>
                  <strong>Stap</strong>

                  <span>{dossier.current_step}</span>
                </div>
              </div>

              {/* ACTIONS */}

              <div className="dossiers-page__actions">
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/dossiers/${dossier._id}`)}
                >
                  <HiOutlineEye />
                  Open
                </button>

                {!isCoach && (
                  <button type="button" onClick={() => openEditModal(dossier)}>
                    <HiOutlinePencilSquare />
                    Bewerken
                  </button>
                )}
              </div>
            </PageCard>
          ))}
        </div>
      )}

      {/* =====================================================
          PAGINATION
      ===================================================== */}

      {pagination?.pages > 1 && (
        <div className="dossiers-page__pagination">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={handlePreviousPage}
          >
            Vorige
          </button>

          <span>
            Pagina {pagination.page} van {pagination.pages}
          </span>

          <button
            type="button"
            disabled={pagination.page >= pagination.pages}
            onClick={handleNextPage}
          >
            Volgende
          </button>
        </div>
      )}

      {/* =====================================================
          EDIT MODAL
      ===================================================== */}

      <DossierEditModal
        isOpen={isEditModalOpen}
        dossier={selectedDossier}
        onClose={() => {
          setIsEditModalOpen(false);

          setSelectedDossier(null);
        }}
      />
    </div>
  );
}
